import React, { useState, useEffect } from 'react';
import { getDatabase, ref, get, push, set, update, remove } from 'firebase/database';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './ReportAnalysis.css';

const CreditorLedger = () => {
  const [creditors, setCreditors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState({ start: '', end: '' });
  const [showForm, setShowForm] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [editingId, setEditingId] = useState(null);
  const [viewingCreditor, setViewingCreditor] = useState(null);
  const [entries, setEntries] = useState([{
    id: Date.now(),
    date: new Date().toISOString().split('T')[0],
    creditorName: '',
    creditorContact: '',
    creditAmount: '',
    dueDate: '',
    referenceNo: '',
    description: ''
  }]);
  const [submitting, setSubmitting] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(0);

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    fetchCreditors();
  }, []);

  const fetchCreditors = async () => {
    try {
      const db = getDatabase();
      const creditorsRef = ref(db, 'HTAMS/expenses/creditorledger');
      const snapshot = await get(creditorsRef);

      if (snapshot.exists()) {
        const data = snapshot.val();
        const creditorsList = Object.keys(data).map(key => ({
          id: key,
          ...data[key]
        }));
        setCreditors(creditorsList);
      } else {
        setCreditors([]);
      }
    } catch (error) {
      console.error('Error fetching creditors:', error);
      toast.error('Failed to fetch creditor data');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e, index) => {
    const { name, value } = e.target;
    
    // Validation for contact
    if (name === 'creditorContact') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 10) {
        setEntries(prev => prev.map((entry, i) => 
          i === index ? { ...entry, [name]: numericValue } : entry
        ));
      }
      return;
    }
    
    // Validation for credit amount
    if (name === 'creditAmount') {
      const numericValue = value.replace(/[^\d.]/g, '');
      const parts = numericValue.split('.');
      const validValue = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : numericValue;
      setEntries(prev => prev.map((entry, i) => 
        i === index ? { ...entry, [name]: validValue } : entry
      ));
      return;
    }
    
    // Validation for reference number (alphanumeric)
    if (name === 'referenceNo') {
      const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, '');
      setEntries(prev => prev.map((entry, i) => 
        i === index ? { ...entry, [name]: alphanumericValue } : entry
      ));
      return;
    }
    
    setEntries(prev => prev.map((entry, i) => 
      i === index ? { ...entry, [name]: value } : entry
    ));
  };

  const addNewEntry = () => {
    const newIndex = entries.length;
    setEntries(prev => [...prev, {
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      creditorName: '',
      creditorContact: '',
      creditAmount: '',
      dueDate: '',
      referenceNo: '',
      description: ''
    }]);
    setExpandedEntry(newIndex);
  };

  const removeEntry = (index) => {
    if (entries.length > 1) {
      setEntries(prev => prev.filter((_, i) => i !== index));
      setExpandedEntry(index > 0 ? index - 1 : 0);
    } else {
      toast.warning('At least one entry is required');
    }
  };

  const toggleEntry = (index) => {
    setExpandedEntry(expandedEntry === index ? index : index);
  };

  const validateEntry = (entry, index) => {
    const entryNum = index + 1;
    
    // Date validation
    if (!entry.date) {
      toast.error(`Entry ${entryNum}: Date is required`);
      return false;
    }
    
    // Creditor name validation
    if (!entry.creditorName || entry.creditorName.trim() === '') {
      toast.error(`Entry ${entryNum}: Creditor name is required`);
      return false;
    }
    
    // Contact validation
    if (entry.creditorContact && entry.creditorContact.trim()) {
      if (!/^\d{10}$/.test(entry.creditorContact.trim())) {
        toast.error(`Entry ${entryNum}: Creditor contact must be 10 digits`);
        return false;
      }
    }
    
    // Credit amount validation
    if (!entry.creditAmount || entry.creditAmount.trim() === '') {
      toast.error(`Entry ${entryNum}: Credit amount is required`);
      return false;
    }
    
    if (parseFloat(entry.creditAmount) <= 0) {
      toast.error(`Entry ${entryNum}: Credit amount must be greater than 0`);
      return false;
    }
    
    return true;
  };

  const resetForm = () => {
    setEntries([{
      id: Date.now(),
      date: new Date().toISOString().split('T')[0],
      creditorName: '',
      creditorContact: '',
      creditAmount: '',
      dueDate: '',
      referenceNo: '',
      description: ''
    }]);
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation for all entries
    for (let i = 0; i < entries.length; i++) {
      if (!validateEntry(entries[i], i)) {
        setExpandedEntry(i); // Expand the entry with validation error
        return;
      }
    }

    setSubmitting(true);

    try {
      const db = getDatabase();
      
      if (editingId) {
        // Update existing creditor (single entry)
        const entry = entries[0];
        const creditorData = {
          date: entry.date,
          creditorName: entry.creditorName,
          creditorContact: entry.creditorContact,
          creditAmount: parseFloat(entry.creditAmount),
          dueDate: entry.dueDate,
          referenceNo: entry.referenceNo,
          description: entry.description
        };

        const creditorRef = ref(db, `HTAMS/expenses/creditorledger/${editingId}`);
        await update(creditorRef, creditorData);
        toast.success('Creditor updated successfully!');
        setEditingId(null);
      } else {
        // Create new creditors (multiple entries)
        let successCount = 0;
        
        for (const entry of entries) {
          const creditorData = {
            date: entry.date,
            creditorName: entry.creditorName,
            creditorContact: entry.creditorContact,
            creditAmount: parseFloat(entry.creditAmount),
            dueDate: entry.dueDate,
            referenceNo: entry.referenceNo,
            description: entry.description
          };

          const creditorsRef = ref(db, 'HTAMS/expenses/creditorledger');
          const newCreditorRef = push(creditorsRef);
          await set(newCreditorRef, creditorData);
          successCount++;
        }
        
        toast.success(`${successCount} creditor ${successCount > 1 ? 'entries' : 'entry'} added successfully!`);
      }

      resetForm();
      fetchCreditors();
    } catch (error) {
      console.error('Error saving creditor:', error);
      toast.error('Failed to save creditor');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (creditor) => {
    setEntries([{
      id: creditor.id,
      date: creditor.date,
      creditorName: creditor.creditorName,
      creditorContact: creditor.creditorContact,
      creditAmount: creditor.creditAmount.toString(),
      dueDate: creditor.dueDate,
      referenceNo: creditor.referenceNo,
      description: creditor.description
    }]);
    setEditingId(creditor.id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, name) => {
    if (window.confirm(`Are you sure you want to delete creditor "${name}"?`)) {
      try {
        const db = getDatabase();
        const creditorRef = ref(db, `HTAMS/expenses/creditorledger/${id}`);
        await remove(creditorRef);
        toast.success('Creditor deleted successfully!');
        fetchCreditors();
      } catch (error) {
        console.error('Error deleting creditor:', error);
        toast.error('Failed to delete creditor');
      }
    }
  };

  const handleView = (creditor) => {
    setViewingCreditor(creditor);
  };

  const closeViewModal = () => {
    setViewingCreditor(null);
  };

  const handleCancelEdit = () => {
    resetForm();
  };

  const filteredCreditors = creditors.filter(creditor => {
    const matchesSearch = creditor.creditorName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          creditor.referenceNo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDate = (!dateFilter.start || new Date(creditor.date) >= new Date(dateFilter.start)) &&
                       (!dateFilter.end || new Date(creditor.date) <= new Date(dateFilter.end));
    return matchesSearch && matchesDate;
  });

  const totalPayable = filteredCreditors.reduce((sum, creditor) => sum + (parseFloat(creditor.creditAmount) || 0), 0);

  const exportToExcel = () => {
    if (filteredCreditors.length === 0) {
      toast.warning('No data to export');
      return;
    }

    const exportData = filteredCreditors.map((creditor, index) => ({
      'Sr No': index + 1,
      'Date': formatDate(creditor.date),
      'Creditor Name': creditor.creditorName || '-',
      'Creditor Contact': creditor.creditorContact || '-',
      'Credit Amount': creditor.creditAmount,
      'Due Date': formatDate(creditor.dueDate),
      'Reference No': creditor.referenceNo || '-',
      'Description': creditor.description || '-'
    }));

    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row => 
        headers.map(header => {
          const value = row[header];
          const escaped = String(value).replace(/\"/g, '\"\"');
          return `\"${escaped}\"`;
        }).join(',')
      )
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Creditor_Ledger_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success(`Exported ${filteredCreditors.length} entries successfully!`);
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentCreditors = filteredCreditors.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCreditors.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const getPageNumbers = () => {
    const pages = [];
    const showPages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(showPages / 2));
    let endPage = Math.min(totalPages, startPage + showPages - 1);

    if (endPage - startPage + 1 < showPages) {
      startPage = Math.max(1, endPage - showPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, dateFilter]);

  if (loading) return <div className="loading">Loading creditor ledger...</div>;

  return (
    <div className="report-analysis-container">
      <ToastContainer position="top-right" autoClose={3000} />
      
      <div className="page-header">
        <h2>Creditor Ledger</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            className="add-btn"
            onClick={exportToExcel}
            style={{ background: '#28a745' }}
          >
            📥 Export to Excel
          </button>
          <button 
            className="add-btn"
            onClick={() => {
              if (showForm && editingId) {
                handleCancelEdit();
              } else {
                setShowForm(!showForm);
              }
            }}
          >
            {showForm ? '✕ Close' : '+ Add Creditor'}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="form-container">
          <h3 className="form-title">{editingId ? 'Edit Creditor' : 'Add New Creditor'}</h3>
          <form onSubmit={handleSubmit} className="purchase-form">
            {entries.map((entry, index) => {
              const isExpanded = expandedEntry === index;
              return (
                <div 
                  key={entry.id} 
                  style={{ 
                    marginBottom: '10px', 
                    border: isExpanded ? '2px solid #2c5f2d' : '2px solid #e0e0e0', 
                    borderRadius: '8px', 
                    overflow: 'hidden',
                    transition: 'all 0.3s ease'
                  }}
                >
                  <div 
                    onClick={() => toggleEntry(index)}
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center', 
                      padding: '10px 15px',
                      background: isExpanded ? '#2c5f2d' : '#f8f9fa',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                      <h4 style={{ margin: 0, color: isExpanded ? 'white' : '#2c5f2d', fontWeight: 'bold' }}>
                        Entry {index + 1}
                      </h4>
                      {entry.creditorName && (
                        <span style={{ fontSize: '14px', color: isExpanded ? '#d4edda' : '#666' }}>
                          {entry.creditorName}
                        </span>
                      )}
                      {entry.creditAmount && (
                        <span style={{ fontSize: '14px', color: isExpanded ? '#d4edda' : '#666', fontWeight: 'bold' }}>
                          ₹{entry.creditAmount}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      {!editingId && entries.length > 1 && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeEntry(index);
                          }}
                          style={{
                            background: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '5px 15px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Remove
                        </button>
                      )}
                      <span style={{ color: isExpanded ? 'white' : '#2c5f2d', fontSize: '20px', fontWeight: 'bold' }}>
                        {isExpanded ? '−' : '+'}
                      </span>
                    </div>
                  </div>
                  
                  {isExpanded && (
                    <div style={{ padding: '15px' }}>
            <div className="form-grid" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: '15px' }}>
              <div className="form-group">
                <label>Date <span className="required">*</span></label>
                <input
                  type="date"
                  name="date"
                  value={entry.date}
                  onChange={(e) => handleInputChange(e, index)}
                  required
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Creditor Name <span className="required">*</span></label>
                <input
                  type="text"
                  name="creditorName"
                  value={entry.creditorName}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Enter creditor name"
                  required
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Creditor Contact</label>
                <input
                  type="text"
                  name="creditorContact"
                  value={entry.creditorContact}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Enter contact number"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Credit Amount <span className="required">*</span></label>
                <input
                  type="text"
                  name="creditAmount"
                  value={entry.creditAmount}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Enter credit amount"
                  required
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  name="dueDate"
                  value={entry.dueDate}
                  onChange={(e) => handleInputChange(e, index)}
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Reference No</label>
                <input
                  type="text"
                  name="referenceNo"
                  value={entry.referenceNo}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Enter reference/transaction number"
                  className="form-control"
                />
              </div>

              <div className="form-group" style={{ gridColumn: 'span 1' }}>
                <label>Description</label>
                <textarea
                  name="description"
                  value={entry.description}
                  onChange={(e) => handleInputChange(e, index)}
                  placeholder="Enter description or notes"
                  rows="3"
                  className="form-control"
                />
              </div>
            </div>
                    </div>
                  )}
                </div>
              );
            })}

            {!editingId && (
              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={addNewEntry}
                  style={{
                    background: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '10px 20px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 'bold'
                  }}
                >
                  + Add Another Entry
                </button>
              </div>
            )}

            <div className="form-actions">
              <button 
                type="button" 
                onClick={handleCancelEdit}
                className="btn-cancel"
              >
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={submitting}
                className="btn-submit"
              >
                {submitting ? 'Saving...' : (editingId ? 'Update Creditor' : `Save ${entries.length} Creditor${entries.length > 1 ? 's' : ''}`)}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="filters">
        <input
          type="text"
          placeholder="Search by creditor name or reference..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
          style={{ flex: '2' }}
        />
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Start Date</label>
          <input
            type="date"
            value={dateFilter.start}
            onChange={(e) => setDateFilter({...dateFilter, start: e.target.value})}
            className="date-input"
          />
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
          <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>End Date</label>
          <input
            type="date"
            value={dateFilter.end}
            onChange={(e) => setDateFilter({...dateFilter, end: e.target.value})}
            className="date-input"
          />
        </div>
        
        {(searchTerm || dateFilter.start || dateFilter.end) && (
          <button
            onClick={() => {
              setSearchTerm('');
              setDateFilter({ start: '', end: '' });
            }}
            style={{
              background: '#dc3545',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              cursor: 'pointer',
              fontSize: '14px',
              whiteSpace: 'nowrap'
            }}
          >
            Clear Filters
          </button>
        )}
      </div>

      <div className="summary-cards">
        <div className="summary-card">
          <h3>Total Payable</h3>
          <p>₹{totalPayable.toLocaleString()}</p>
        </div>
      </div>

      <div className="table-container">
        <table className="report-table">
          <thead>
            <tr>
              <th>Sr No</th>
              <th>Date</th>
              <th>Creditor Name</th>
              <th>Contact</th>
              <th>Credit Amount</th>
              <th>Due Date</th>
              <th>Reference No</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {currentCreditors.length > 0 ? (
              currentCreditors.map((creditor, index) => (
                <tr key={creditor.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{formatDate(creditor.date)}</td>
                  <td>{creditor.creditorName}</td>
                  <td>{creditor.creditorContact || '-'}</td>
                  <td>₹{parseFloat(creditor.creditAmount)?.toLocaleString()}</td>
                  <td>{formatDate(creditor.dueDate) || '-'}</td>
                  <td>{creditor.referenceNo || '-'}</td>
                  <td>
                    <div className="action-buttons">
                      <button 
                        className="action-btn view-btn"
                        onClick={() => handleView(creditor)}
                        title="View Details"
                      >
                        👁️
                      </button>
                      <button 
                        className="action-btn edit-btn"
                        onClick={() => handleEdit(creditor)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        className="action-btn delete-btn"
                        onClick={() => handleDelete(creditor.id, creditor.creditorName)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : null}
          </tbody>
        </table>
        
        {currentCreditors.length === 0 && (
          <div className="empty-state">
            <div className="empty-icon">📒</div>
            <h3>No creditor entries found</h3>
            <p>Add creditor entries using the "Add Creditor" button above.</p>
          </div>
        )}
      </div>

      {/* View Modal */}
      {viewingCreditor && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Creditor Details</h3>
              <button className="modal-close" onClick={closeViewModal}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item">
                  <label>Date:</label>
                  <span>{formatDate(viewingCreditor.date)}</span>
                </div>
                <div className="detail-item">
                  <label>Creditor Name:</label>
                  <span>{viewingCreditor.creditorName}</span>
                </div>
                <div className="detail-item">
                  <label>Contact:</label>
                  <span>{viewingCreditor.creditorContact || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Credit Amount:</label>
                  <span className="highlight-amount">₹{parseFloat(viewingCreditor.creditAmount)?.toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Due Date:</label>
                  <span>{formatDate(viewingCreditor.dueDate) || 'N/A'}</span>
                </div>
                <div className="detail-item">
                  <label>Reference No:</label>
                  <span>{viewingCreditor.referenceNo || 'N/A'}</span>
                </div>
                {viewingCreditor.description && (
                  <div className="detail-item full-width">
                    <label>Description:</label>
                    <span>{viewingCreditor.description}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {filteredCreditors.length > 0 && (
        <div className="pagination-container">
          <div className="pagination-info">
            Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredCreditors.length)} of {filteredCreditors.length} entries
          </div>
          <div className="pagination">
            <button
              onClick={() => paginate(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-btn"
            >
              Previous
            </button>
            {getPageNumbers().map(number => (
              <button
                key={number}
                onClick={() => paginate(number)}
                className={`pagination-btn ${currentPage === number ? 'active' : ''}`}
              >
                {number}
              </button>
            ))}
            <button
              onClick={() => paginate(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-btn"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreditorLedger;
